"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { LiquidSurface } from "../liquid-surface";
import { type CircleActionVariant } from "../circle-action-variants";
import { Z_INDEX } from "../z-index";

export interface SidebarLiquidButtonProps {
  icon: React.ReactNode;
  label: React.ReactNode;
  variant?: CircleActionVariant;
  active?: boolean;
  disabled?: boolean;
  /** Fuerza el label visible; útil para padres/hijos de flyout. */
  expanded?: boolean;
  /** Botón compacto para submenús inline. */
  compact?: boolean;
  /** Dirección de crecimiento del label respecto al ícono. */
  expandTo?: "left" | "right";
  onClick?: () => void;
  className?: string;
  "aria-label": string;
  "aria-expanded"?: boolean;
}

const BUTTON_SIZE = 32;
const ICON_SIZE = 16;
const OPEN_MS = 520;
const CLOSE_MS = 220;
const EASE = "cubic-bezier(0.16,1,0.3,1)";

type SidebarLiquidParticle = {
  id: number;
  x: number;
  y: number;
  size: number;
  delay: number;
  duration: number;
  opacity: number;
  rotate: number;
};

export function SidebarLiquidButton({
  icon,
  label,
  variant = "ocean",
  active = false,
  disabled = false,
  expanded: expandedProp,
  compact = false,
  expandTo = "right",
  onClick,
  className,
  "aria-label": ariaLabel,
  "aria-expanded": ariaExpanded
}: SidebarLiquidButtonProps) {
  const [hovered, setHovered] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [particles, setParticles] = React.useState<SidebarLiquidParticle[]>([]);
  const expanded = Boolean(expandedProp || hovered || focused) && !disabled;
  const isIdle = !active && !expanded;
  const durationMs = expanded ? OPEN_MS : CLOSE_MS;
  const buttonSize = compact ? 28 : BUTTON_SIZE;
  const iconSize = compact ? 14 : ICON_SIZE;
  const iconInset = (buttonSize - iconSize) / 2;

  const spawnParticles = React.useCallback(() => {
    setParticles(
      Array.from({ length: 9 }, (_, index) => ({
        id: Date.now() + index,
        x: 10 + Math.random() * 80,
        y: 16 + Math.random() * 68,
        size: 8 + Math.random() * 14,
        delay: Math.random() * 150,
        duration: 560 + Math.random() * 320,
        opacity: 0.16 + Math.random() * 0.24,
        rotate: -28 + Math.random() * 56
      }))
    );
  }, []);

  React.useEffect(() => {
    if (!expanded) {
      setParticles([]);
      return;
    }

    spawnParticles();
  }, [expanded, spawnParticles]);

  return (
    <span
      className={cn(
        compact
          ? "sidebar-liquid-button-slot relative inline-block h-7 w-7 overflow-visible align-top"
          : "sidebar-liquid-button-slot relative inline-block h-8 w-8 overflow-visible align-top",
        expanded && "sidebar-liquid-button-slot--expanded"
      )}
      onMouseEnter={() => {
        setHovered(true);
        spawnParticles();
      }}
      onMouseLeave={() => setHovered(false)}
      onFocus={(event) => {
        const target = event.target as HTMLElement | null;
        // Evita dejar el botón "expandido" por foco de mouse tras click.
        // Solo mantenemos expansión por foco cuando es focus-visible (teclado).
        setFocused(Boolean(target?.matches(":focus-visible")));
      }}
      onBlur={() => setFocused(false)}
    >
      <LiquidSurface
        as="button"
        type="button"
        variant={variant}
        tone={isIdle ? "neutral" : "tinted"}
        shape="pill"
        size="free"
        shadow="neutral"
        surfaceStyle={isIdle ? "sidebarInactive" : "default"}
        edgeStyle={isIdle ? "flat" : "softGlow"}
        bodyStyle="flat"
        cursorGlow={false}
        cursorGlowVisible={false}
        hoverDeform={false}
        hoverBounce={false}
        disabled={disabled}
        onClick={() => onClick?.()}
        aria-label={ariaLabel}
        aria-expanded={ariaExpanded}
        className={cn(
          compact
            ? "sidebar-liquid-button group/sidebar-liquid absolute top-0 h-7 min-w-7 overflow-hidden border-0"
            : "sidebar-liquid-button group/sidebar-liquid absolute top-0 h-8 min-w-8 overflow-hidden border-0",
          "inline-flex items-center whitespace-nowrap font-bold tracking-[-0.01em]",
          compact ? "text-[11px]" : "text-[12px]",
          active || expanded
            ? "!text-white"
            : "text-[var(--gb-accent-color)] dark:text-[var(--gb-accent-color-dark)]",
          expanded && "sidebar-liquid-button--expanded",
          active && "sidebar-liquid-button--active",
          isIdle && "sidebar-liquid-button--inactive",
          compact && "sidebar-liquid-button--compact",
          "focus-visible:ring-offset-0",
          expandTo === "right" ? "justify-start" : "justify-end",
          className
        )}
        style={{
          [expandTo === "right" ? "left" : "right"]: 0,
          width: expanded ? "max-content" : `${buttonSize}px`,
          maxWidth: expanded ? "min(18rem, calc(100vw - 4rem))" : `${buttonSize}px`,
          height: `${buttonSize}px`,
          zIndex: expanded ? Z_INDEX.DROPDOWN : undefined,
          padding: 0,
          paddingLeft:
            expandTo === "right"
              ? `${iconInset}px`
              : expanded
                ? compact ? "10px" : "14px"
                : `${iconInset}px`,
          paddingRight:
            expandTo === "right"
              ? expanded
                ? compact ? "10px" : "14px"
                : `${iconInset}px`
              : `${iconInset}px`,
          gap: expanded ? "8px" : "0px",
          transform: expanded ? (compact ? "scale(1.035, 1.018)" : "scale(1.045, 1.025)") : "scale(1)",
          transformOrigin: expandTo === "right" ? "left center" : "right center",
          filter: compact
            ? expanded
              ? "saturate(1.12) brightness(0.92) contrast(1.06)"
              : "saturate(1.08) brightness(0.96) contrast(1.04)"
            : expanded
              ? "saturate(1.08) brightness(1.02)"
              : undefined,
          transition: [
            `width ${durationMs}ms ${EASE}`,
            `max-width ${durationMs}ms ${EASE}`,
            `padding-left ${durationMs}ms ${EASE}`,
            `padding-right ${durationMs}ms ${EASE}`,
            `gap ${durationMs}ms ${EASE}`,
            `transform ${durationMs}ms ${EASE}`,
            `filter ${durationMs}ms ${EASE}`
          ].join(",")
        }}
      >
        <span
          aria-hidden="true"
          className="sidebar-liquid-button__active-fill pointer-events-none absolute inset-0"
          style={{
            opacity: active || expanded ? 1 : 0,
            transform: active || expanded ? "scale(1)" : "scale(0.78, 0.9)",
            transformOrigin: "center center",
            boxShadow: active || expanded
              ? "inset 0 0 0 1px rgba(var(--cab-shadow-rgb), 0.62)"
              : "inset 0 0 0 1px rgba(var(--cab-shadow-rgb), 0)",
            filter: active || expanded
              ? "saturate(1.12) brightness(1.02) contrast(1.02) blur(0px)"
              : "saturate(1.06) brightness(1.0) contrast(1.0) blur(1.8px)",
            transition: [
              `transform ${durationMs + 140}ms cubic-bezier(0.22,1,0.36,1)`,
              `opacity ${Math.min(durationMs, 240)}ms ease`,
              `filter ${durationMs + 120}ms cubic-bezier(0.16,1,0.3,1)`,
              `box-shadow ${Math.min(durationMs, 260)}ms ease ${active || expanded ? 150 : 0}ms`
            ].join(",")
          }}
        />

        <span
          aria-hidden="true"
          className="sidebar-liquid-button__active-shine pointer-events-none absolute inset-y-0 w-[72%]"
          style={{
            left: expandTo === "right" ? "-68%" : undefined,
            right: expandTo === "left" ? "-68%" : undefined,
            opacity: expanded ? 1 : 0,
            transform: expanded
              ? `translateX(${expandTo === "right" ? "240%" : "-240%"}) skewX(-18deg)`
              : "translateX(0) skewX(-18deg)",
            transition: [
              `transform ${durationMs + 160}ms cubic-bezier(0.16,1,0.3,1)`,
              `opacity ${Math.min(durationMs, 260)}ms ease`
            ].join(",")
          }}
        />

        {particles.map((particle) => (
          <span
            key={particle.id}
            aria-hidden="true"
            className="sidebar-liquid-button__particle pointer-events-none absolute flex items-center justify-center text-white"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              "--sidebar-particle-opacity": particle.opacity,
              "--sidebar-particle-rotate": `${particle.rotate}deg`,
              animationDelay: `${particle.delay}ms`,
              animationDuration: `${particle.duration}ms`
            } as React.CSSProperties}
          >
            {icon}
          </span>
        ))}

        <span
          className={cn(
            "relative inline-flex min-w-0 items-center",
            expandTo === "left" && "flex-row-reverse"
          )}
          style={{ gap: "inherit" }}
        >
          <span
            className="sidebar-liquid-button__icon flex shrink-0 items-center justify-center leading-none transition-colors duration-200"
            style={{ width: iconSize, height: iconSize }}
            aria-hidden="true"
          >
            {icon}
          </span>
          <span
            className="sidebar-liquid-button__label overflow-hidden whitespace-nowrap text-current transition-colors duration-200"
            style={{
              maxWidth: expanded ? "13rem" : "0px",
              opacity: expanded ? 1 : 0,
              transition: [
                `max-width ${durationMs}ms ${EASE}`,
                `opacity ${Math.min(durationMs, 240)}ms ease`
              ].join(",")
            }}
          >
            {label}
          </span>
        </span>
      </LiquidSurface>

      <style jsx global>{`
        @keyframes sidebar-liquid-particle-pop {
          0% {
            opacity: 0;
            transform: translate(-50%, -50%) rotate(var(--sidebar-particle-rotate, 0deg)) scale(0.45);
            filter: blur(1px) saturate(1.1);
          }
          24% {
            opacity: var(--sidebar-particle-opacity, 0.24);
            transform: translate(-50%, -50%) rotate(var(--sidebar-particle-rotate, 0deg)) scale(1.08);
            filter: blur(0px) saturate(1.2);
          }
          58% {
            opacity: calc(var(--sidebar-particle-opacity, 0.24) * 0.78);
            transform: translate(-50%, -50%) rotate(var(--sidebar-particle-rotate, 0deg)) scale(0.92) translateY(-3px);
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -50%) rotate(var(--sidebar-particle-rotate, 0deg)) scale(1.34) translateY(-9px);
            filter: blur(1.5px) saturate(1.0);
          }
        }

        .sidebar-liquid-button__active-fill {
          position: absolute !important;
          z-index: 1 !important;
          /* Dejamos más margen interno para que el rim del LiquidSurface
             (edgeStyle=softGlow) sea visible en el borde externo. */
          inset: 1px !important;
          border-radius: inherit;
          background:
            radial-gradient(ellipse 90% 120% at 8% 0%, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0.07) 28%, transparent 60%),
            linear-gradient(180deg, rgba(255,255,255,0.11) 0%, transparent 50%, rgba(15,23,42,0.09) 100%),
            var(--cab-hover-bg);
          filter: saturate(1.12) brightness(1.01) contrast(1.02);
          will-change: transform, opacity, filter;
        }

        /* Evita el "snap" del borde de softGlow al expandir: el borde de color
           lo aporta la capa active-fill, que sí entra de forma animada. */
        .sidebar-liquid-button--expanded.liquid-surface,
        .sidebar-liquid-button--active.liquid-surface {
          box-shadow:
            0 1px 2px rgba(15,23,42,0.015),
            0 4px 10px -6px rgba(15,23,42,0.02) !important;
        }

        :global(.dark) .sidebar-liquid-button--inactive.liquid-surface {
          filter: none !important;
        }

        /* Hover / expand en dark: lift sutil sobre idle, sin flash blanco */
        :global(.dark) .sidebar-liquid-button--expanded.liquid-surface {
          color: var(--gb-accent-color-dark) !important;
          background:
            radial-gradient(ellipse 74% 92% at 8% 8%, transparent 0%, transparent 22%, transparent 54%),
            radial-gradient(ellipse 34% 108% at 112% 50%, rgba(var(--cab-shadow-rgb), 0.09) 0%, rgba(var(--cab-shadow-rgb), 0.045) 36%, transparent 62%),
            radial-gradient(ellipse 82% 58% at 48% 18%, transparent 0%, color-mix(in srgb, var(--gb-tint-bg) 10%, rgba(32,32,40,0.45)) 42%, transparent 72%),
            radial-gradient(ellipse 86% 46% at 72% 112%, rgba(var(--cab-shadow-rgb), 0.05) 0%, transparent 76%),
            linear-gradient(
              180deg,
              color-mix(in srgb, var(--gb-tint-bg) 42%, rgba(38,38,46,0.88)) 0%,
              color-mix(in srgb, var(--gb-tint-bg) 46%, rgba(30,30,38,0.90)) 64%,
              color-mix(in srgb, var(--gb-tint-bg) 50%, rgba(22,22,28,0.92)) 100%
            ) !important;
          backdrop-filter: blur(20px) saturate(1.06) brightness(0.74) contrast(1.02) !important;
          -webkit-backdrop-filter: blur(20px) saturate(1.06) brightness(0.74) contrast(1.02) !important;
          box-shadow:
            inset -1px -1px 0 rgba(255,255,255,0.04),
            inset -1px  0   0 rgba(255,255,255,0.012),
            inset  0   -1px 0 color-mix(in srgb, var(--gb-accent-color-dark) 6%, rgba(2,6,23,0.38)),
            inset  0    0   0 1.5px color-mix(in srgb, var(--gb-tint-border) 20%, rgba(15,23,42,0.58)),
            inset  1px  1px 0 rgba(0,0,0,0.22),
            0 1px 3px -1px rgba(0,0,0,0.16),
            0 4px 10px -6px rgba(0,0,0,0.14),
            var(--gb-tint-shadow) !important;
        }

        .dark .sidebar-liquid-button__active-fill {
          background:
            radial-gradient(ellipse 90% 120% at 8% 0%, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 28%, transparent 60%),
            linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(15,23,42,0.08) 50%, rgba(2,6,23,0.14) 100%),
            var(--cab-active-bg);
          filter: saturate(1.14) brightness(1.00) contrast(1.02);
        }

        .dark .sidebar-liquid-button--inactive.sidebar-liquid-button--expanded .sidebar-liquid-button__active-fill {
          background:
            radial-gradient(ellipse 90% 120% at 8% 0%, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.025) 28%, transparent 60%),
            linear-gradient(180deg, rgba(255,255,255,0.04) 0%, transparent 50%, rgba(2,6,23,0.14) 100%),
            var(--cab-hover-bg);
          filter: saturate(1.08) brightness(0.86) contrast(1.02);
        }

        .sidebar-liquid-button--compact.sidebar-liquid-button--inactive {
          color: color-mix(in srgb, var(--gb-accent-color) 88%, #020617) !important;
        }

        .dark .sidebar-liquid-button--compact.sidebar-liquid-button--inactive {
          color: color-mix(in srgb, var(--gb-accent-color-dark) 90%, #000000) !important;
        }

        .sidebar-liquid-button--compact.sidebar-liquid-button--expanded,
        .sidebar-liquid-button--compact.sidebar-liquid-button--active,
        .dark .sidebar-liquid-button--compact.sidebar-liquid-button--expanded,
        .dark .sidebar-liquid-button--compact.sidebar-liquid-button--active {
          color: #ffffff !important;
        }

        .sidebar-liquid-button--compact .sidebar-liquid-button__active-fill {
          inset: 1px !important;
          background:
            linear-gradient(180deg, rgba(15,23,42,0.08) 0%, rgba(15,23,42,0.13) 48%, rgba(15,23,42,0.18) 100%),
            var(--cab-active-bg);
          filter: saturate(1.14) brightness(0.90) contrast(1.06);
        }

        .dark .sidebar-liquid-button--compact .sidebar-liquid-button__active-fill {
          background:
            linear-gradient(180deg, rgba(2,6,23,0.12) 0%, rgba(2,6,23,0.18) 50%, rgba(2,6,23,0.24) 100%),
            var(--cab-active-bg);
          filter: saturate(1.14) brightness(0.86) contrast(1.08);
        }

        .sidebar-liquid-button__active-shine {
          position: absolute !important;
          z-index: 2 !important;
          border-radius: inherit;
          background: linear-gradient(
            100deg,
            transparent 0%,
            rgba(255,255,255,0.00) 16%,
            rgba(255,255,255,0.48) 44%,
            rgba(255,255,255,0.14) 60%,
            transparent 100%
          );
          mix-blend-mode: screen;
          filter: blur(0.35px);
        }

        :global(.dark) .sidebar-liquid-button--expanded .sidebar-liquid-button__active-shine {
          background: linear-gradient(
            100deg,
            transparent 0%,
            rgba(255,255,255,0.00) 18%,
            rgba(255,255,255,0.14) 44%,
            rgba(255,255,255,0.04) 62%,
            transparent 100%
          );
          mix-blend-mode: soft-light;
          opacity: 0.5;
        }

        .sidebar-liquid-button__particle {
          position: absolute !important;
          z-index: 2 !important;
          animation-name: sidebar-liquid-particle-pop;
          animation-timing-function: cubic-bezier(0.16,1,0.3,1);
          animation-fill-mode: both;
          mix-blend-mode: screen;
        }

        .sidebar-liquid-button__particle svg {
          width: 100%;
          height: 100%;
          color: currentColor !important;
          stroke: currentColor !important;
        }

        .sidebar-liquid-button__particle img,
        .sidebar-liquid-button__particle .sidebar-icon {
          width: 100%;
          height: 100%;
          filter: brightness(0) invert(1) !important;
          opacity: 1 !important;
        }

        .sidebar-liquid-button__icon svg {
          color: currentColor;
          stroke: currentColor;
        }

        .sidebar-liquid-button__icon img,
        .sidebar-liquid-button__icon .sidebar-icon {
          filter: var(--cab-icon-filter-idle);
          opacity: 1;
        }

        .dark .sidebar-liquid-button__icon img,
        .dark .sidebar-liquid-button__icon .sidebar-icon {
          filter: var(--cab-icon-filter-idle-dark);
        }

        .sidebar-liquid-button--expanded .sidebar-liquid-button__icon,
        .sidebar-liquid-button--active .sidebar-liquid-button__icon,
        .sidebar-liquid-button--expanded .sidebar-liquid-button__label,
        .sidebar-liquid-button--active .sidebar-liquid-button__label {
          color: #fff !important;
        }

        .sidebar-liquid-button--expanded .sidebar-liquid-button__icon svg,
        .sidebar-liquid-button--active .sidebar-liquid-button__icon svg {
          color: #fff !important;
          stroke: #fff !important;
        }

        .sidebar-liquid-button--expanded .sidebar-liquid-button__icon img,
        .sidebar-liquid-button--active .sidebar-liquid-button__icon img {
          filter: brightness(0) invert(1) !important;
          opacity: 1 !important;
        }
      `}</style>
    </span>
  );
}
